import { axiosInstance } from '../../App'
import {
  CalendarHolidayAuditItem,
  CalendarHolidayImportResult,
  CalendarHolidayItem,
  CalendarHolidayManualPayload,
  CalendarHolidayOfficialItem,
} from '../../interface/CalendarHolidayTypes'

type UnknownRecord = Record<string, unknown>
type Primitive = string | number | boolean | null | undefined

interface BasicFilterDTO {
  filters?: BasicFilter[]
}

interface BasicFilter {
  fieldName?: string
  values?: Primitive[]
}

export interface CalendarHolidayListQueryParams {
  multiSortMeta?: unknown
  filterDTO?: unknown
  cardFilterDTO?: unknown
  dateFrom?: string | null
  dateTo?: string | null
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : -1
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : -1
}

function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1' || normalized === 't' || normalized === 'yes'
  }
  return false
}

function toStringSafe(value: unknown): string {
  return typeof value === 'string' ? value : String(value ?? '')
}

function normalizeForSearch(value: unknown): string {
  return toStringSafe(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function getFilterDTO(params: CalendarHolidayListQueryParams = {}): BasicFilterDTO | null {
  const raw = params.filterDTO ?? params.cardFilterDTO ?? null
  if (!isRecord(raw)) return null
  const filters = Array.isArray(raw.filters) ? raw.filters : []
  return { filters: filters.filter((item) => isRecord(item)) as BasicFilter[] }
}

function cloneFilterDTO(filterDTO: BasicFilterDTO | null): BasicFilterDTO | null {
  if (!filterDTO?.filters) return null
  return {
    ...filterDTO,
    filters: [...filterDTO.filters],
  }
}

function splitTextFilters(filterDTO: BasicFilterDTO | null, fields: string[]) {
  const source = cloneFilterDTO(filterDTO)
  const keepInBackend: BasicFilter[] = []
  const applyInFrontend: BasicFilter[] = []
  const normalizedFields = new Set(fields.map((field) => field.toLowerCase()))

  for (const filter of source?.filters ?? []) {
    const field = toStringSafe(filter.fieldName).toLowerCase()
    if (normalizedFields.has(field)) {
      applyInFrontend.push(filter)
    } else {
      keepInBackend.push(filter)
    }
  }

  const backendFilterDTO = keepInBackend.length > 0
    ? { ...(source ?? {}), filters: keepInBackend }
    : null

  return { backendFilterDTO, frontendFilters: applyInFrontend }
}

function getFilterValue(filter: BasicFilter): string {
  const values = Array.isArray(filter.values) ? filter.values : []
  return toStringSafe(values[0] ?? '').trim()
}

function applyHolidayTextFilters(items: CalendarHolidayItem[], filters: BasicFilter[]): CalendarHolidayItem[] {
  if (filters.length === 0) return items

  return items.filter((item) => {
    for (const filter of filters) {
      const filterValue = normalizeForSearch(getFilterValue(filter))
      if (!filterValue) continue

      const field = toStringSafe(filter.fieldName).toLowerCase()
      const candidate = field === 'reason' ? item.reason : item.description
      const normalizedCandidate = normalizeForSearch(candidate)
      if (!normalizedCandidate.includes(filterValue)) {
        return false
      }
    }
    return true
  })
}

function formatAuditDate(dateText: string): string {
  if (!dateText) return ''
  const date = new Date(dateText)
  if (Number.isNaN(date.getTime())) return dateText
  return date.toLocaleString('es-AR')
}

function formatHolidayDate(dateText: string): string {
  if (!dateText) return ''
  const date = new Date(`${dateText}T00:00:00`)
  if (Number.isNaN(date.getTime())) return dateText
  return date.toLocaleDateString('es-AR')
}

function applyAuditTextFilters(items: CalendarHolidayAuditItem[], filters: BasicFilter[]): CalendarHolidayAuditItem[] {
  if (filters.length === 0) return items

  return items.filter((item) => {
    for (const filter of filters) {
      const filterValue = normalizeForSearch(getFilterValue(filter))
      if (!filterValue) continue

      const field = toStringSafe(filter.fieldName).toLowerCase()
      let candidate = ''

      if (field === 'date') {
        candidate = formatAuditDate(item.date)
      } else if (field === 'holidaydate') {
        candidate = formatHolidayDate(item.holidaydate)
      } else if (field === 'eventdesc') {
        candidate = item.eventdesc
      } else if (field === 'useraudit') {
        candidate = item.useraudit
      } else {
        continue
      }

      if (!normalizeForSearch(candidate).includes(filterValue)) {
        return false
      }
    }

    return true
  })
}

function normalizeHoliday(raw: unknown): CalendarHolidayItem | null {
  if (!isRecord(raw)) return null
  const id = toNumber(raw.holiday)
  const date = toStringSafe(raw.date)
  if (id <= 0 || date.trim() === '') return null

  return {
    holiday: id,
    date,
    reason: toStringSafe(raw.reason),
    description: toStringSafe(raw.description),
  }
}

function normalizeOfficialHoliday(raw: unknown): CalendarHolidayOfficialItem | null {
  if (!isRecord(raw)) return null
  const date = toStringSafe(raw.date)
  if (date.trim() === '') return null

  const holidayId = toNumber(raw.holiday)

  return {
    holiday: holidayId > 0 ? holidayId : null,
    date,
    reason: toStringSafe(raw.reason),
    description: toStringSafe(raw.description),
  }
}

function normalizeAuditItem(raw: unknown): CalendarHolidayAuditItem | null {
  if (!isRecord(raw)) return null

  const auditId = toNumber(raw.auditevent)
  const holidayId = toNumber(raw.holiday)
  if (auditId <= 0 || holidayId <= 0) return null

  return {
    auditevent: auditId,
    holiday: holidayId,
    date: toStringSafe(raw.date),
    eventdesc: toStringSafe(raw.eventdesc),
    useraudit: toStringSafe(raw.useraudit),
    holidaydate: toStringSafe(raw.holidaydate),
    holidaydatemod: toBoolean(raw.holidaydatemod),
    reason: toStringSafe(raw.reason),
    reasonmod: toBoolean(raw.reasonmod),
    description: toStringSafe(raw.description),
    descriptionmod: toBoolean(raw.descriptionmod),
  }
}

function normalizeImportResult(raw: unknown): CalendarHolidayImportResult {
  if (!isRecord(raw)) {
    return { imported: 0, rejected: 0 }
  }
  return {
    imported: Math.max(0, toNumber(raw.imported)),
    rejected: Math.max(0, toNumber(raw.rejected)),
  }
}

export function useCalendarHolidayService() {
async function getCalendarHolidays(
  offSet: number,
  pageSize: number,
  params: CalendarHolidayListQueryParams = {},
): Promise<CalendarHolidayItem[]> {
  const filterDTO = getFilterDTO(params)
  const { backendFilterDTO, frontendFilters } = splitTextFilters(filterDTO, ['reason', 'description'])
  const fetchPageSize = Math.max(pageSize, 500)

  const response = await axiosInstance.post('/api/secured/calendar-holiday/list', {
    loadConfigDTO: {
      offSet: 0,
      pageSize: fetchPageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters: backendFilterDTO,
    },
    dateFrom: params.dateFrom ?? null,
    dateTo: params.dateTo ?? null,
  })
  const array = Array.isArray(response.data) ? response.data : []
  const normalized = array
    .map(normalizeHoliday)
    .filter((item): item is CalendarHolidayItem => item !== null)

  const filtered = applyHolidayTextFilters(normalized, frontendFilters)
  return filtered.slice(offSet, offSet + pageSize)
}

async function createManualHoliday(payload: CalendarHolidayManualPayload): Promise<CalendarHolidayItem> {
  const response = await axiosInstance.post('/api/secured/calendar-holiday/save-manual', payload)
  const normalized = normalizeHoliday(response.data)
  if (!normalized) {
    throw new Error('La respuesta de creacion de feriado es invalida')
  }
  return normalized
}

async function updateManualHoliday(id: number, payload: CalendarHolidayManualPayload): Promise<CalendarHolidayItem> {
  const response = await axiosInstance.put(`/api/secured/calendar-holiday/${id}`, payload)
  const normalized = normalizeHoliday(response.data)
  if (!normalized) {
    throw new Error('La respuesta de edicion de feriado es invalida')
  }
  return normalized
}

async function deleteHoliday(id: number): Promise<void> {
  await axiosInstance.delete(`/api/secured/calendar-holiday/${id}`)
}

async function getOfficialHolidays(year: number): Promise<CalendarHolidayOfficialItem[]> {
  const response = await axiosInstance.get('/api/secured/calendar-holiday/official', {
    params: { year },
  })
  const array = Array.isArray(response.data) ? response.data : []
  return array
    .map(normalizeOfficialHoliday)
    .filter((item): item is CalendarHolidayOfficialItem => item !== null)
}

async function importOfficialHolidays(holidays: CalendarHolidayOfficialItem[]): Promise<CalendarHolidayImportResult> {
  const response = await axiosInstance.post('/api/secured/calendar-holiday/official/import', { holidays })
  return normalizeImportResult(response.data)
}

async function getCalendarHolidayAudit(
  offSet: number,
  pageSize: number,
  params: CalendarHolidayListQueryParams = {},
): Promise<CalendarHolidayAuditItem[]> {
  const filterDTO = getFilterDTO(params)
  const frontendFilters = filterDTO?.filters ?? []

  const response = await axiosInstance.get('/api/secured/calendar-holiday/audit', {
    params: { offSet: 0, pageSize: Math.max(pageSize, 2000) },
  })
  const array = Array.isArray(response.data) ? response.data : []
  const normalized = array
    .map(normalizeAuditItem)
    .filter((item): item is CalendarHolidayAuditItem => item !== null)

  const filtered = applyAuditTextFilters(normalized, frontendFilters)
  return filtered.slice(offSet, offSet + pageSize)
}

  return {
    getCalendarHolidays,
    createManualHoliday,
    updateManualHoliday,
    deleteHoliday,
    getOfficialHolidays,
    importOfficialHolidays,
    getCalendarHolidayAudit,
  }
}
